import { useMemo } from "react";

import { isNil } from "lodash";

import { FORMATTERS } from "app/common/benchmark/formatters";
import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";

import { getDomainsData, getProgressBarValue, sortByDomainOrder } from "../../../helpers/helpers";
import useTrafficSearchApiParams from "../../../hooks/useApiParamsSearch";
import { useTrafficData } from "../../../services/search-overview-api/apiHooks";
import type { SearchTrafficDataSchema } from "../../../services/search-overview-api/types";
import type { DomainInfo } from "../../../types";

type TransformedSearchTrafficData = {
    domain: DomainInfo;
    searchTraffic: {
        formattedValue?: string;
        progressBarValue: number;
        color: string;
    };
    yoyChange?: number;
    percentageOf?: string;
}[];

const transformSearchTrafficData = (data: SearchTrafficDataSchema): TransformedSearchTrafficData => {
    if (!data?.TrafficSourcesVisits) {
        return [];
    }

    const visits = data.TrafficSourcesVisits;
    const domainsDataMap = getDomainsData();
    const getSiteColor = chosenSitesService.getSiteColor;

    const sumOfDomainsTotal = Object.values(visits).reduce((acc, domainMetrics) => {
        const total = domainMetrics?.All?.Total || 0;
        return acc + total;
    }, 0);

    const transformed = Object.entries(visits).reduce((acc, [domain, domainMetrics]) => {
        if (!domainMetrics) {
            return acc;
        }

        const allTraffic = domainMetrics.All;
        const percentageOf = getProgressBarValue(allTraffic?.Total, allTraffic?.TotalVisits);

        acc.push({
            domain: domainsDataMap[domain],
            searchTraffic: {
                formattedValue: !isNil(allTraffic?.Average) ? FORMATTERS.numberAbbr(allTraffic.Average) : undefined,
                progressBarValue: sumOfDomainsTotal ? (allTraffic.Total / sumOfDomainsTotal) * 100 : 0,
                color: getSiteColor(domain),
            },
            yoyChange: allTraffic?.YoyChange,
            percentageOf: percentageOf?.formattedValue,
        });

        return acc;
    }, [] as TransformedSearchTrafficData);

    return sortByDomainOrder(transformed, (item) => item.domain.name);
};

const useSearchTrafficTable = () => {
    const { trafficSearchApiParams } = useTrafficSearchApiParams();
    const { data, isLoading, isError } = useTrafficData(trafficSearchApiParams);

    const transformedData = useMemo(() => transformSearchTrafficData(data), [data]);

    return { data: transformedData, isLoading, isError };
};

export default useSearchTrafficTable;
